/**
 * The session paths of the contract (SDD-019 S-2), checked against the GENERATED `paths`: a
 * renamed proto path is a compile error here and nowhere else — no other file may spell a session
 * path (T-7). Tiny and static on purpose: `createAuthServiceClient` needs `isSessionEndpoint` for
 * every client, while the session API itself (`./session`) is loaded lazily.
 */
import type { paths } from './generated/auth.v1';

type SessionPath = Extract<keyof paths, `/v1/session:${string}`>;

/** Every session path of the contract, checked against the generated `paths`. */
export const SESSION_PATHS = {
  exchange: '/v1/session:exchange',
  refresh: '/v1/session:refresh',
  end: '/v1/session:end',
} as const satisfies Record<string, SessionPath>;

// The reverse check: a session path the contract gains and this table misses is a compile error.
type Covered = (typeof SESSION_PATHS)[keyof typeof SESSION_PATHS];
const everyPathIsCovered: Exclude<SessionPath, Covered> extends never ? true : never = true;
void everyPathIsCovered;

/**
 * True for a URL that is one of the session endpoints — what `createEisClient`'s
 * `isSessionEndpoint` wants. Matched on the path's end, so it holds behind any mount prefix
 * (`/api/auth/v1/session:refresh` locally, `/auth/v1/…` on the API host).
 */
export function isSessionEndpoint(url: string): boolean {
  let pathname: string;
  try {
    pathname = new URL(url, 'http://relative.invalid').pathname;
  } catch {
    return false;
  }
  return Object.values(SESSION_PATHS).some(
    (path) =>
      pathname === path || pathname.endsWith(path) || decodeURIComponent(pathname).endsWith(path),
  );
}
